import React, { useEffect, useState, useContext } from "react";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";
import companyBasedRequest from "../../../request/companyBasedRequest";
import "./teamServicesModal.css";

const TeamServicesModal = ({ isOpen, onClose, shopServices = [], staffProfile = null, onSave }) => {
    const { language, userData } = useContext(CombinedContext);
    const [rows, setRows] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isOpen || !staffProfile) return;

        const staffServiceIds = staffProfile.services.map((s) => s.serviceId || s);
        const staffSet = new Set(staffServiceIds);

        const mapped = shopServices.map((svc) => ({
            serviceId: svc.serviceId,
            name: svc.name,
            price: svc.price,
            currency: svc.currency,
            duration: svc.duration,
            isHomeService: svc.isHomeService,
            active: staffSet.has(svc.serviceId),
        }));

        setRows(mapped);
    }, [isOpen, shopServices, staffProfile]);

    useEffect(() => {
        if (!isOpen) setRows([]);
    }, [isOpen]);

    const updateRow = (index, patch) => {
        setRows((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], ...patch };
            return next;
        });
    };

    const handleSave = async () => {
        const serviceIds = rows.filter((r) => r.active).map((r) => r.serviceId);

        try {
            setSaving(true);

            await companyBasedRequest.updateUserServices(
                staffProfile.staffId,
                serviceIds,
                userData.cognitoTokens.AccessToken
            );

            onSave?.(serviceIds);

            onClose(); // close AFTER success
        } catch (err) {
            console.error("Failed to save staff services", err);
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen || !staffProfile) return null;

    return (
        <div className="sf-team-services-overlay" onClick={onClose}>
            <div className="sf-team-services-modal" onClick={(e) => e.stopPropagation()}>
                {/* HEADER */}
                <div className="sf-team-services-header">
                    <h2 className="sf-team-services-title">Manage Services for {staffProfile.fullName}</h2>
                    <button className="sf-team-services-close" onClick={onClose}>
                        ✕
                    </button>
                </div>

                {/* BODY */}
                <div className="sf-team-services-body">
                    {rows.map((row, idx) => (
                        <div key={row.serviceId} className="sf-tsm-row">
                            <div className="sf-tsm-name">
                                {row.name}
                                {row.isHomeService && (
                                    <>
                                        <span className="sf-tsm-separator"> · </span>
                                        {translations[language].teamPage.main.homeService}
                                    </>
                                )}
                            </div>
                            <div className="sf-tsm-meta">
                                <span>
                                    {row.currency || "USD"} {row.price ?? 0}
                                </span>

                                <span>{row.duration} min</span>
                            </div>

                            <button
                                className={`sf-tsm-toggle ${row.active ? "active" : ""}`}
                                onClick={() => updateRow(idx, { active: !row.active })}
                            >
                                {row.active
                                    ? translations[language].teamPage.main.active || "Active"
                                    : translations[language].teamPage.main.inactive || "Inactive"}
                            </button>
                        </div>
                    ))}
                </div>

                {/* FOOTER (MATCHES EXCLUSION MODAL STRUCTURE) */}
                <div className="sf-base-footer">
                    <button className="btn ghost" onClick={onClose}>
                        {translations[language].calendar.editor.cancel}
                    </button>

                    <button className="btn" onClick={handleSave} disabled={saving}>
                        {saving
                            ? translations[language].calendar.editor.saving
                            : translations[language].calendar.editor.save}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TeamServicesModal;
